/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import axios from 'axios';
import { toast } from 'react-toastify';
import { enrichPersonWithPolling } from 'src/app/utils/contactEnrich';

const API = `${process.env.REACT_APP_API_URL}`;

// ─── Veri sağlayıcıları (Apollo / RocketReach) ───────────────────────────────
// Backend iki sağlayıcıyı da aynı IOrg/IPerson şekline map'ler; modal yalnızca
// /v1/apollo/* ↔ /v1/rocketreach/* önekini değiştirir.
type Provider = 'apollo' | 'rocketreach';
const PROVIDERS: { key: Provider; label: string; emoji: string }[] = [
  { key: 'apollo', label: 'Apollo.io', emoji: '🛰️' },
  { key: 'rocketreach', label: 'RocketReach', emoji: '🚀' },
];

// ─── Tipler ──────────────────────────────────────────────────────────────────
interface IOrg {
  id: string;
  name: string;
  domain?: string | null;
  website_url?: string | null;
  logo_url?: string | null;
  linkedin_url?: string | null;
  industry?: string | null;
  estimated_num_employees?: number | null;
  phone?: string | null;
  city?: string | null;
  country?: string | null;
  founded_year?: number | null;
}

interface IPerson {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  title?: string | null;
  email?: string | null;
  email_locked: boolean;
  email_status?: string | null;
  photo_url?: string | null;
  linkedin_url?: string | null;
  city?: string | null;
  country?: string | null;
  seniority?: string | null;
  organization_name?: string | null;
  organization_phone?: string | null;
  // Apollo anahtarı tam yetkili değilken (api_search) dönen maskelenmiş kayıtlar
  obfuscated?: boolean;
  has_email?: boolean;
  has_direct_phone?: boolean;
}

interface IPagination {
  page: number;
  per_page?: number;
  total_entries: number;
  total_pages: number;
}

// Potansiyel müşteri kaydı oluşturulduktan sonra ilgili kişi olarak yazılacak taslak
export interface PendingApolloContact {
  key: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  notes: string;
  organization_name?: string | null;
}

interface Props {
  show: boolean;
  companyName: string;
  initialSelected: PendingApolloContact[];
  onClose: () => void;
  onConfirm: (contacts: PendingApolloContact[]) => void;
}

// ─── Tasarım token'ları (contact-finder ile aynı dil) ────────────────────────
const T = {
  card: '#ffffff',
  border: '#e6e8f2',
  text: '#1e2235',
  sub: '#7c81a1',
  accent: '#6366f1',
  accentSoft: '#eef0ff',
  green: '#12b76a',
  greenSoft: '#e9f9f1',
  amber: '#f79009',
  amberSoft: '#fef4e6',
};

const TITLE_PRESETS: { label: string; emoji: string; titles: string[] }[] = [
  { label: 'Tümü', emoji: '👥', titles: [] },
  {
    label: 'Yönetim',
    emoji: '👑',
    titles: ['CEO', 'Founder', 'Co-Founder', 'Owner', 'General Manager', 'Genel Müdür', 'Managing Director', 'Chairman', 'Genel Müdür Yardımcısı', 'Yönetim Kurulu'],
  },
  {
    label: 'Bilgi Teknolojileri',
    emoji: '💻',
    titles: ['CTO', 'IT Manager', 'IT Director', 'Information Technology', 'Bilgi Teknolojileri', 'Bilgi İşlem', 'System Administrator', 'Sistem Yöneticisi', 'IT Specialist'],
  },
  {
    label: 'Satın Alma',
    emoji: '🛒',
    titles: ['Purchasing Manager', 'Procurement', 'Satın Alma', 'Satın Alma Müdürü', 'Satın Alma Uzmanı', 'Buyer', 'Supply Chain'],
  },
  {
    label: 'Finans',
    emoji: '💰',
    titles: ['CFO', 'Finance Manager', 'Finans Müdürü', 'Finans', 'Muhasebe', 'Accounting', 'Mali İşler'],
  },
  {
    label: 'İnsan Kaynakları',
    emoji: '🤝',
    titles: ['HR Manager', 'Human Resources', 'İnsan Kaynakları', 'İK Müdürü', 'People Operations'],
  },
];

// "X BİLGİ TEK. SAN. VE TİC. LTD. ŞTİ." → Apollo'da aranabilir sade ad
const LEGAL_TOKENS = new Set([
  'a.ş',
  'a.ş.',
  'aş',
  'a.s',
  'a.s.',
  'ltd',
  'ltd.',
  'şti',
  'şti.',
  'sti',
  'sti.',
  'san',
  'san.',
  'tic',
  'tic.',
  've',
  'sanayi',
  'ticaret',
  'limited',
  'şirketi',
  'sirketi',
  'anonim',
  'ith',
  'ith.',
  'ihr',
  'ihr.',
  'ithalat',
  'ihracat',
  'paz',
  'paz.',
  'pazarlama',
  'dış',
  'dis',
  'iç',
  'ic',
]);
export const cleanCompanyName = (raw: string): string => {
  const words = (raw || '').trim().split(/\s+/);
  while (words.length > 1 && LEGAL_TOKENS.has(words[words.length - 1].toLocaleLowerCase('tr-TR'))) {
    words.pop();
  }
  return words.join(' ');
};

// ─── Küçük yapı taşları ──────────────────────────────────────────────────────
const AVATAR_COLORS = ['#6366f1', '#0ea5e9', '#12b76a', '#f79009', '#ef5da8', '#8b5cf6', '#14b8a6'];

const Avatar: React.FC<{ name: string; src?: string | null; size?: number }> = ({ name, src, size = 46 }) => {
  const [broken, setBroken] = useState(false);
  const initials = (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toLocaleUpperCase('tr-TR'))
    .join('');
  const color = AVATAR_COLORS[(name || '').length % AVATAR_COLORS.length];
  if (src && !broken) {
    return (
      <img src={src} alt={name} onError={() => setBroken(true)} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${T.border}` }} />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `${color}18`,
        color,
        fontWeight: 800,
        fontSize: size * 0.36,
        letterSpacing: 0.5,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
};

const Badge: React.FC<{ bg: string; color: string; children: React.ReactNode }> = ({ bg, color, children }) => (
  <span
    style={{
      background: bg,
      color,
      borderRadius: 999,
      padding: '3px 10px',
      fontSize: 11.5,
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const EmailStatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  if (status === 'verified')
    return (
      <Badge bg={T.greenSoft} color={T.green}>
        ✓ Doğrulanmış
      </Badge>
    );
  if (status === 'guessed' || status === 'extrapolated')
    return (
      <Badge bg={T.amberSoft} color={T.amber}>
        ~ Tahmini
      </Badge>
    );
  return null;
};

// Kişide e-posta ya da telefon sinyali var mı? (firma santrali herkes için ortak
// olduğundan filtreye dahil edilmez — yalnızca kişiye ait sinyaller sayılır)
const hasContactSignal = (p: IPerson) => !!p.email || !!p.has_email || !!p.has_direct_phone;

// ─── Modal ───────────────────────────────────────────────────────────────────
const ApolloContactPicker: React.FC<Props> = ({ show, companyName, initialSelected, onClose, onConfirm }) => {
  const [query, setQuery] = useState('');
  const [provider, setProvider] = useState<Provider>('apollo');
  const prov = PROVIDERS.find((pp) => pp.key === provider)!;
  // RocketReach'e özel opsiyonel konum filtresi
  const [locationFilter, setLocationFilter] = useState('');
  const [orgs, setOrgs] = useState<IOrg[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgSearched, setOrgSearched] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<IOrg | null>(null);

  const [people, setPeople] = useState<IPerson[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState<IPagination | null>(null);
  const [limitedMode, setLimitedMode] = useState(false);
  const [presetIdx, setPresetIdx] = useState(0);
  const [onlyWithContact, setOnlyWithContact] = useState(true);
  const [enrichingIds, setEnrichingIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<PendingApolloContact[]>([]);

  // Modal her açıldığında firma adıyla otomatik ara
  const searchedForRef = useRef<string | null>(null);

  const searchOrganizations = async (q: string, prv: Provider = provider) => {
    const cleaned = (q || '').trim();
    if (cleaned.length < 2) {
      toast.warning('Aramak için en az 2 karakter girin.', { autoClose: 2000 });
      return;
    }
    setOrgLoading(true);
    setOrgSearched(true);
    setSelectedOrg(null);
    setPeople([]);
    setPagination(null);
    try {
      const res = await axios.post(`${API}/${prv}/organizations/search`, { q: cleaned });
      setOrgs(res.data?.organizations || []);
    } catch (err: any) {
      setOrgs([]);
      toast.error(err?.response?.data?.message || 'Firma araması başarısız oldu.', { autoClose: 3500 });
    } finally {
      setOrgLoading(false);
    }
  };

  const changeProvider = (p: Provider) => {
    if (p === provider) return;
    setProvider(p);
    setOrgs([]);
    setOrgSearched(false);
    setSelectedOrg(null);
    setPeople([]);
    setPagination(null);
    setLimitedMode(false);
    setPresetIdx(0);
    setLocationFilter('');
    if (query.trim().length >= 2) searchOrganizations(query, p);
  };

  useEffect(() => {
    if (!show) return;
    setSelected(initialSelected);
    const cleaned = cleanCompanyName(companyName);
    // Aynı firma adıyla tekrar açıldığında aramayı yenilemeyip önceki
    // firma/çalışan listesini koruyoruz ("Düzenle" ile geri dönüş senaryosu)
    if (cleaned.length >= 2 && searchedForRef.current !== cleaned) {
      searchedForRef.current = cleaned;
      setQuery(cleaned);
      searchOrganizations(cleaned);
    }
  }, [show, companyName]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPeople = async (org: IOrg, page: number, titles: string[], append: boolean, locationOverride?: string) => {
    if (append) setLoadingMore(true);
    else setPeopleLoading(true);
    // "Temizle" senaryosunda state güncellemesi asenkron kaldığından override ile geçilir
    const loc = (locationOverride !== undefined ? locationOverride : locationFilter).trim();
    try {
      const res = await axios.post(`${API}/${provider}/people/search`, {
        organization_id: org.id,
        organization_name: org.name,
        ...(provider === 'rocketreach' && org.domain ? { domain: org.domain } : {}),
        ...(provider === 'rocketreach' && loc ? { location: loc } : {}),
        titles,
        page,
        per_page: 12,
      });
      const incoming: IPerson[] = res.data?.people || [];
      setLimitedMode(!!res.data?.limited);
      setPagination(res.data?.pagination || null);
      setPeople((prev) => {
        if (!append) return incoming;
        const known = new Set(prev.map((p) => p.id));
        return [...prev, ...incoming.filter((p) => !known.has(p.id))];
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Çalışan araması başarısız oldu.', { autoClose: 3500 });
    } finally {
      setPeopleLoading(false);
      setLoadingMore(false);
    }
  };

  const selectOrg = (o: IOrg) => {
    setSelectedOrg(o);
    setPresetIdx(0);
    setPeople([]);
    fetchPeople(o, 1, [], false);
  };

  const changePreset = (idx: number) => {
    setPresetIdx(idx);
    if (selectedOrg) fetchPeople(selectedOrg, 1, TITLE_PRESETS[idx].titles, false);
  };

  const enrichPerson = async (person: IPerson) => {
    setEnrichingIds((s) => new Set(s).add(person.id));
    try {
      // RocketReach sonucu geç hazırlarsa yardımcı /people/enrich/status ucunu yoklar
      const enriched = await enrichPersonWithPolling<IPerson>(
        provider,
        {
          id: person.id,
          first_name: person.first_name,
          last_name: person.last_name,
          organization_name: selectedOrg?.name || person.organization_name,
          domain: selectedOrg?.domain || undefined,
        },
        (info) => toast.info(info.message || `${prov.label} kişi bilgisini hazırlıyor, bekleniyor…`, { autoClose: 4000 }),
      );
      setPeople((prev) => prev.map((p) => (p.id === person.id ? { ...p, ...enriched, id: p.id, obfuscated: false, email_locked: !enriched.email } : p)));
      if (enriched?.email) {
        toast.success('Kişi bilgileri ve e-posta getirildi! 🎉', { autoClose: 1800 });
      } else {
        toast.warning(`Kişi bilgileri getirildi ancak ${prov.label} e-posta bulamadı.`, { autoClose: 2500 });
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'E-posta getirilemedi.', { autoClose: 4000 });
    } finally {
      setEnrichingIds((s) => {
        const n = new Set(s);
        n.delete(person.id);
        return n;
      });
    }
  };

  // Kişisel numara Apollo'da webhook gerektirdiğinden firma santralini yedek olarak kullanıyoruz
  const phoneOf = (p: IPerson) => p.organization_phone || selectedOrg?.phone || null;

  const toggleSelect = (p: IPerson) => {
    setSelected((prev) => {
      if (prev.some((c) => c.key === p.id)) return prev.filter((c) => c.key !== p.id);
      const notesParts = [`${prov.label} İletişim Bulucu ile eklendi`];
      if (selectedOrg?.name) notesParts.push(`Firma: ${selectedOrg.name}`);
      if (p.title) notesParts.push(`Ünvan: ${p.title}`);
      if (p.linkedin_url) notesParts.push(`LinkedIn: ${p.linkedin_url}`);
      const phone = phoneOf(p);
      return [
        ...prev,
        {
          key: p.id,
          full_name: p.name.slice(0, 255),
          email: p.email ? p.email.slice(0, 255) : null,
          phone: phone ? phone.slice(0, 20) : null,
          title: p.title ? p.title.slice(0, 100) : null,
          notes: notesParts.join(' | '),
          organization_name: selectedOrg?.name || p.organization_name || null,
        },
      ];
    });
  };

  const visiblePeople = onlyWithContact ? people.filter(hasContactSignal) : people;
  const hiddenCount = people.length - visiblePeople.length;

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} centered size="xl" backdrop="static" scrollable className="apc-modal" backdropClassName="apc-backdrop">
      <style>{`
        /* Arka planı — altta açık duran "Potansiyel Müşteri Ekle" modalı dahil — bulanıklaştır.
           Bunun için backdrop'un o modalın üstünde, bu modalın da backdrop'un üstünde olması gerekir. */
        .apc-backdrop.modal-backdrop { z-index: 1060; background-color: rgba(15, 17, 30, .45); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
        .apc-backdrop.modal-backdrop.show { opacity: 1; }
        .apc-modal.modal { z-index: 1061; }

        .apc-btn { border: none; cursor: pointer; font-weight: 700; font-family: inherit; display: inline-flex; align-items: center; justify-content: center; gap: 8px; transition: transform .12s, box-shadow .12s, opacity .12s; }
        .apc-btn:disabled { opacity: .55; cursor: not-allowed; }
        .apc-btn:not(:disabled):hover { transform: translateY(-1px); }
        .apc-spin { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: apcSpin .7s linear infinite; }
        .apc-spin--dark { border-color: ${T.accent}44; border-top-color: ${T.accent}; }
        @keyframes apcSpin { to { transform: rotate(360deg); } }
      `}</style>

      <Modal.Header closeButton>
        <Modal.Title>
          İletişim Bilgisi Topla
          <span style={{ color: T.sub, fontSize: 13, fontWeight: 600, marginLeft: 10 }}>{selectedOrg ? selectedOrg.name : 'firma kaydı seçin'}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ color: T.text, maxHeight: '70vh' }}>
        {/* ── Veri sağlayıcı seçimi ── */}
        <div className="d-flex gap-2 mb-4">
          {PROVIDERS.map((pm) => (
            <button key={pm.key} type="button" className={`btn btn-sm ${pm.key === provider ? 'btn-primary' : 'btn-light'}`} onClick={() => changeProvider(pm.key)}>
              {pm.emoji} {pm.label}
            </button>
          ))}
        </div>

        {/* ── Firma arama ── */}
        <div className="input-group mb-4">
          <input
            type="text"
            className="form-control form-control-solid"
            placeholder="Firma adı"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                searchOrganizations(query);
              }
            }}
          />
          <button type="button" className="btn btn-primary" disabled={orgLoading || query.trim().length < 2} onClick={() => searchOrganizations(query)}>
            {orgLoading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : '🔍 Ara'}
          </button>
        </div>

        {/* ── Firma kayıtları ── */}
        {orgLoading ? (
          <div className="text-center py-5" style={{ color: T.sub }}>
            <span className="apc-spin apc-spin--dark me-2" /> {prov.label} veritabanında firma aranıyor...
          </div>
        ) : orgSearched && orgs.length === 0 ? (
          <div className="text-center py-5" style={{ color: T.sub }}>
            <div style={{ fontSize: 34, marginBottom: 8 }}>🏢</div>
            <div style={{ fontWeight: 700, color: T.text, marginBottom: 4 }}>Bu adla firma kaydı bulunamadı</div>
            <div style={{ fontSize: 13.5 }}>Firma adını sadeleştirip tekrar aramayı deneyin.</div>
          </div>
        ) : orgs.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: selectedOrg ? 22 : 0 }}>
            {orgs.map((o) => {
              const active = selectedOrg?.id === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  className="apc-btn"
                  onClick={() => selectOrg(o)}
                  style={{
                    border: `1.5px solid ${active ? T.accent : T.border}`,
                    background: active ? T.accentSoft : T.card,
                    borderRadius: 14,
                    padding: 14,
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    width: '100%',
                    boxShadow: active ? `0 6px 16px ${T.accent}22` : 'none',
                  }}
                >
                  <Avatar name={o.name} src={o.logo_url} size={40} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={o.name}>
                      {o.name}
                    </div>
                    <div style={{ color: T.sub, fontSize: 12.4, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.domain || o.industry || '—'}
                    </div>
                    <div style={{ display: 'flex', gap: 5, marginTop: 7, flexWrap: 'wrap' }}>
                      {o.city ? (
                        <Badge bg="#f2f3f8" color={T.sub}>
                          📍 {o.city}
                        </Badge>
                      ) : null}
                      {o.estimated_num_employees ? (
                        <Badge bg="#f2f3f8" color={T.sub}>
                          👥 {o.estimated_num_employees.toLocaleString('tr-TR')}
                        </Badge>
                      ) : null}
                      {o.phone ? (
                        <Badge bg={T.greenSoft} color={T.green}>
                          📞 {o.phone}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}

        {/* ── Çalışanlar ── */}
        {selectedOrg && (
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 18 }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div style={{ fontWeight: 800, fontSize: 15.5 }}>
                {selectedOrg.name} Çalışanları
                <span style={{ color: T.sub, fontSize: 13, fontWeight: 600, marginLeft: 8 }}>
                  {pagination ? `${pagination.total_entries.toLocaleString('tr-TR')} kişi` : 'aranıyor...'}
                </span>
              </div>
              <label className="form-check form-switch form-check-custom form-check-solid" style={{ fontSize: 13 }}>
                <input className="form-check-input h-20px w-30px" type="checkbox" checked={onlyWithContact} onChange={(e) => setOnlyWithContact(e.target.checked)} />
                <span className="form-check-label" style={{ color: T.sub, fontWeight: 600 }}>
                  Sadece e-posta / telefonu olanlar
                </span>
              </label>
            </div>

            {limitedMode && !peopleLoading && (
              <div
                style={{
                  background: T.accentSoft,
                  border: `1px solid ${T.accent}33`,
                  borderRadius: 12,
                  padding: '11px 15px',
                  marginBottom: 16,
                  fontSize: 12.8,
                  lineHeight: 1.6,
                  color: '#3f45a8',
                }}
              >
                <b>ℹ️ Önizleme modu:</b> Apollo arama sonuçlarını soyadı maskeli ve e-postasız verir — arama <b>kredi harcamaz</b>. Kişiyi listeye ekleyebilmek için önce{' '}
                <b>"Bilgileri Getir"</b> ile açmanız gerekir (kişi başına 1 Apollo kredisi).
              </div>
            )}

            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 16 }}>
              {TITLE_PRESETS.map((p, idx) => {
                const active = presetIdx === idx;
                return (
                  <button
                    key={p.label}
                    type="button"
                    className="apc-btn"
                    onClick={() => changePreset(idx)}
                    disabled={peopleLoading}
                    style={{
                      background: active ? T.accent : '#f2f3f8',
                      color: active ? '#fff' : T.sub,
                      borderRadius: 999,
                      padding: '7px 14px',
                      fontSize: 12.5,
                    }}
                  >
                    {p.emoji} {p.label}
                  </button>
                );
              })}
            </div>

            {/* Konum filtresi (RocketReach — opsiyonel) */}
            {provider === 'rocketreach' && (
              <div className="d-flex gap-2 align-items-center flex-wrap mb-4">
                <input
                  type="text"
                  className="form-control form-control-solid form-control-sm"
                  style={{ maxWidth: 300 }}
                  placeholder="📍 Konum filtresi — opsiyonel (ör. Istanbul)"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (selectedOrg) fetchPeople(selectedOrg, 1, TITLE_PRESETS[presetIdx].titles, false);
                    }
                  }}
                  disabled={peopleLoading}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-light-primary"
                  disabled={peopleLoading}
                  onClick={() => selectedOrg && fetchPeople(selectedOrg, 1, TITLE_PRESETS[presetIdx].titles, false)}
                >
                  📍 Uygula
                </button>
                {locationFilter.trim() ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    disabled={peopleLoading}
                    onClick={() => {
                      setLocationFilter('');
                      if (selectedOrg) fetchPeople(selectedOrg, 1, TITLE_PRESETS[presetIdx].titles, false, '');
                    }}
                  >
                    ✕ Temizle
                  </button>
                ) : null}
              </div>
            )}

            {peopleLoading ? (
              <div className="text-center py-5" style={{ color: T.sub }}>
                <span className="apc-spin apc-spin--dark me-2" /> Çalışanlar getiriliyor...
              </div>
            ) : visiblePeople.length === 0 ? (
              <div className="text-center py-5" style={{ color: T.sub }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>🕵️</div>
                <div style={{ fontWeight: 700, color: T.text, marginBottom: 4 }}>{hiddenCount > 0 ? 'İletişim bilgisi olan kişi bulunamadı' : 'Bu filtreyle kişi bulunamadı'}</div>
                <div style={{ fontSize: 13.5 }}>
                  {hiddenCount > 0
                    ? `${hiddenCount} kişi filtre nedeniyle gizlendi — filtreyi kapatarak görebilirsiniz.`
                    : 'Başka bir departman filtresi deneyin veya "Tümü"ne dönün.'}
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                  {visiblePeople.map((p) => {
                    const enriching = enrichingIds.has(p.id);
                    const isSelected = selected.some((c) => c.key === p.id);
                    const phone = phoneOf(p);
                    return (
                      <div
                        key={p.id}
                        style={{
                          border: `1.5px solid ${isSelected ? T.accent : T.border}`,
                          background: isSelected ? T.accentSoft : '#fff',
                          borderRadius: 14,
                          padding: 16,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                        }}
                      >
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          <Avatar name={p.name} src={p.photo_url} />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 800, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                            <div style={{ color: T.sub, fontSize: 12.4, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.title || ''}>
                              {p.title || 'Ünvan bilinmiyor'}
                            </div>
                            <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
                              <EmailStatusBadge status={p.email_status} />
                              {p.city ? (
                                <Badge bg="#f2f3f8" color={T.sub}>
                                  📍 {p.city}
                                </Badge>
                              ) : null}
                            </div>
                          </div>
                          {p.linkedin_url && (
                            <a
                              href={p.linkedin_url}
                              target="_blank"
                              rel="noreferrer"
                              title="LinkedIn profili"
                              style={{
                                background: '#eef6fb',
                                color: '#0a66c2',
                                width: 32,
                                height: 32,
                                borderRadius: 9,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: 13,
                                textDecoration: 'none',
                                flexShrink: 0,
                              }}
                            >
                              in
                            </a>
                          )}
                        </div>

                        {/* E-posta */}
                        {p.email ? (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              background: T.greenSoft,
                              border: `1px solid ${T.green}33`,
                              borderRadius: 10,
                              padding: '8px 11px',
                            }}
                          >
                            <span style={{ fontSize: 13 }}>✉️</span>
                            <span
                              style={{ fontWeight: 700, fontSize: 12.8, color: '#0b7a4b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={p.email}
                            >
                              {p.email}
                            </span>
                          </div>
                        ) : p.obfuscated ? (
                          <>
                            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                              {p.has_email ? (
                                <Badge bg={T.greenSoft} color={T.green}>
                                  ✉️ E-posta mevcut
                                </Badge>
                              ) : null}
                              {p.has_direct_phone ? (
                                <Badge bg={T.greenSoft} color={T.green}>
                                  📞 Telefon mevcut
                                </Badge>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              className="apc-btn"
                              onClick={() => enrichPerson(p)}
                              disabled={enriching}
                              style={{
                                background: T.accentSoft,
                                color: T.accent,
                                borderRadius: 10,
                                padding: '9px 12px',
                                fontSize: 12.8,
                                border: `1px dashed ${T.accent}55`,
                                width: '100%',
                              }}
                            >
                              {enriching ? (
                                <>
                                  <span className="apc-spin apc-spin--dark" /> Bilgiler getiriliyor...
                                </>
                              ) : (
                                <>🔓 Bilgileri Getir (tam ad + e-posta)</>
                              )}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="apc-btn"
                            onClick={() => enrichPerson(p)}
                            disabled={enriching}
                            style={{
                              background: T.accentSoft,
                              color: T.accent,
                              borderRadius: 10,
                              padding: '9px 12px',
                              fontSize: 12.8,
                              border: `1px dashed ${T.accent}55`,
                              width: '100%',
                            }}
                          >
                            {enriching ? (
                              <>
                                <span className="apc-spin apc-spin--dark" /> Bilgiler getiriliyor...
                              </>
                            ) : (
                              <>🔓 E-postayı Getir</>
                            )}
                          </button>
                        )}

                        {/* Telefon (firma santrali) */}
                        {phone && (
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f7f8fc', border: `1px solid ${T.border}`, borderRadius: 10, padding: '8px 11px' }}
                          >
                            <span style={{ fontSize: 13 }}>📞</span>
                            <span style={{ fontWeight: 700, fontSize: 12.8, color: T.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {phone}
                            </span>
                            <span style={{ color: T.sub, fontSize: 11 }}>{provider === 'rocketreach' && p.organization_phone ? 'direkt tel.' : 'firma tel.'}</span>
                          </div>
                        )}

                        <button
                          type="button"
                          className="apc-btn"
                          onClick={() => toggleSelect(p)}
                          disabled={p.obfuscated}
                          title={p.obfuscated ? 'Maskeli kayıt seçilemez — önce "Bilgileri Getir" ile açın' : ''}
                          style={{
                            background: isSelected ? T.greenSoft : p.obfuscated ? '#f2f3f8' : `linear-gradient(135deg, ${T.accent}, #8b5cf6)`,
                            color: isSelected ? T.green : p.obfuscated ? T.sub : '#fff',
                            borderRadius: 10,
                            padding: '9px 12px',
                            fontSize: 12.8,
                            width: '100%',
                          }}
                        >
                          {isSelected ? '✓ Seçildi — kaldırmak için tıklayın' : p.obfuscated ? '🔒 Listeye Ekle' : '＋ Listeye Ekle'}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {onlyWithContact && hiddenCount > 0 && (
                  <div className="text-center mt-3" style={{ color: T.sub, fontSize: 12.5 }}>
                    İletişim bilgisi görünmeyen {hiddenCount} kişi gizlendi.
                  </div>
                )}

                {pagination && pagination.page < pagination.total_pages && (
                  <div className="d-flex justify-content-center mt-4">
                    <button
                      type="button"
                      className="apc-btn"
                      onClick={() => selectedOrg && fetchPeople(selectedOrg, (pagination?.page || 1) + 1, TITLE_PRESETS[presetIdx].titles, true)}
                      disabled={loadingMore}
                      style={{ background: '#fff', border: `1.5px solid ${T.border}`, color: T.text, borderRadius: 12, padding: '10px 24px', fontSize: 13 }}
                    >
                      {loadingMore ? <span className="apc-spin apc-spin--dark" /> : '⬇️'} Daha Fazla Yükle
                      <span style={{ color: T.sub, fontWeight: 600 }}>
                        ({people.length}/{pagination.total_entries.toLocaleString('tr-TR')})
                      </span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between align-items-center">
        <div style={{ color: T.sub, fontSize: 13 }}>
          {selected.length > 0 ? (
            <>
              <b style={{ color: T.accent }}>{selected.length} kişi</b> seçildi · müşteri kaydedilince ilgili kişi olarak eklenecek
            </>
          ) : (
            'Kayıt sonrası eklenecek kişileri seçin'
          )}
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-light" onClick={onClose}>
            İptal
          </button>
          <button type="button" className="btn btn-primary" onClick={handleConfirm}>
            Seçilenleri Kullan {selected.length > 0 ? `(${selected.length})` : ''}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ApolloContactPicker;
